/* One reconciliation definition, as the design draws it: the two sides, the runs, and the four panels
 * that govern how a run is judged, scheduled and recorded. One run's breaks open in a drawer over this
 * page rather than on a page of their own.
 *
 * **The drawer has an address.** `/reconciliation/:definitionId/runs/:runId` renders this page with
 * the drawer open, so a break list somebody was sent still opens, Back closes it, and the page under it
 * is the definition the run belongs to rather than a guess.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DatasetKindIcon } from '@/features/catalog-browser';
import type { Run } from '@/shared/api/generated/reconciliation';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { personName } from '@/shared/model/person';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useConnections } from '@/entities/connection';
import {
  can,
  CONFIGURE_RECONCILIATION_PERMISSION,
  CONFIGURE_RUN_SCHEDULE_PERMISSION,
  PUBLISH_RECONCILIATION_PERMISSION,
  RUN_RECONCILIATION_PERMISSION,
  useIdentity,
} from '@/entities/session';

import { useDefinition, useRequestRun, useRuns } from '../api/reconciliation';
import { breaksPath, definitionPath } from '../model/paths';
import {
  breakTotalOf,
  formatCount,
  formatJaccard,
  hasBreaks,
  isInFlight,
  jaccardNoteKey,
  outcomeOf,
} from '../model/reconciliation';
import { BreakPolicyPanel } from './BreakPolicyPanel';
import { BreaksDrawer } from './BreaksDrawer';
import { DefinitionHistory } from './DefinitionHistory';
import { RunSchedulePanel } from './RunSchedulePanel';
import { RunStateTag } from './RunStateTag';
import { TolerancePanel } from './TolerancePanel';

/** A count as the design prints it — compact — with the exact figure on hover.
 *
 * Absent rather than zero for a run that measured nothing: a refused or failed run did not find zero
 * mismatches, it found nothing, and a column of zeros would make it read as a clean reconciliation. */
function Count({ value }: { readonly value: number | null }): ReactElement {
  const { t } = useTranslation();
  if (value === null)
    return <span className="lq-muted">{t('reconciliation.noFigures')}</span>;
  return (
    <span className="lq-rdf__count" title={formatCount(value)}>
      {compactNumber(value)}
    </span>
  );
}

function JaccardCell({ run }: { readonly run: Run }): ReactElement {
  const { t } = useTranslation();
  const estimate = run.jaccard_estimate;
  const noteKey = jaccardNoteKey(run);

  if (estimate === null || estimate === undefined || noteKey === null) {
    return <span className="lq-muted">{t('reconciliation.noFigures')}</span>;
  }
  /* The note says whether the figure was measured by the join or is the estimate a refusal was made
     on. It is on hover because the design has no room for it in the cell, and it is not optional:
     the same 0.0000 means "the keys share nothing" on one row and is the gate's evidence on another. */
  return (
    <span className="lq-rdf__jaccard lq-mono" title={t(noteKey)}>
      {formatJaccard(estimate)}
    </span>
  );
}

export function DefinitionRuns(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { definitionId = '', runId } = useParams<{
    definitionId: string;
    runId: string;
  }>();
  const definition = useDefinition(definitionId);
  const runs = useRuns(definitionId);
  const request = useRequestRun();
  const identity = useIdentity();
  const connections = useConnections();
  const [queued, setQueued] = useState<string | null>(null);

  const mayRun = can(identity.data, RUN_RECONCILIATION_PERMISSION);
  const mayPublish = can(identity.data, PUBLISH_RECONCILIATION_PERMISSION);
  /* Three grants rather than one. Configuring the break policy is an edit on the reconciliation; the
     schedule is its own type. A deployment may grant "reconcile now" and withhold "reconcile every
     night for ever", so the panels ask separately. */
  const mayConfigurePolicy = can(identity.data, CONFIGURE_RECONCILIATION_PERMISSION);
  const mayConfigureSchedule = can(identity.data, CONFIGURE_RUN_SCHEDULE_PERMISSION);

  const name = definition.isSuccess ? definition.data.name : null;
  const version = definition.isSuccess ? definition.data.tolerance_version : null;
  const inFlight = runs.isSuccess && runs.data.items.some((run) => isInFlight(run.state));
  const now = new Date();

  /* A connection's name, never its id: a bare UUID in the line a reader scans first is the defect
     this codebase has recorded four times. */
  const connectionName = (id: string): string =>
    connections.data?.items.find((connection) => connection.id === id)?.name ??
    t('reconciliation.connection.unknown');

  /* Always the button, disabled when it cannot be used, with the reason on hover — the design's
     shape. A run the server would refuse with a 409 is not offered as if it would succeed. */
  const why = !mayRun
    ? t('reconciliation.requestRun.needs', { permission: RUN_RECONCILIATION_PERMISSION })
    : inFlight
      ? t('reconciliation.requestRun.inFlight')
      : undefined;
  const requestControl = (
    <Button
      tone="primary"
      title={why}
      disabled={!mayRun || inFlight || request.isPending || !definition.isSuccess}
      onClick={() => {
        setQueued(null);
        request.mutate(definitionId, {
          onSuccess: (run) => {
            setQueued(run.id);
          },
        });
      }}
    >
      {t('reconciliation.requestRun')}
    </Button>
  );

  const runItems = runs.isSuccess ? runs.data.items : [];

  return (
    <div className="lq-rc lq-stack">
      <div>
        <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
          <Link to={SCREEN_PATHS.reconciliation}>{t('reconciliation.title')}</Link>
          <span aria-hidden="true" className="lq-breadcrumbs__sep">
            ›
          </span>
          <span aria-current="page">
            {name ?? t('reconciliation.definition.loading')}
          </span>
        </nav>
        <PageHeader
          title={name ?? t('reconciliation.definition.loading')}
          lede={t('reconciliation.runs.lede')}
          actions={requestControl}
        />
      </div>

      {queued !== null && (
        /* The version is named because it is frozen onto the run: a later edit to the definition
           will not re-judge it, which is the one thing a reader may reasonably not expect. */
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">{t('reconciliation.queued.title')}</strong>
          <span className="lq-flash__body">
            {version === null
              ? t('reconciliation.queued.bodyNoVersion')
              : t('reconciliation.queued.body', { version: version.version })}
          </span>
        </div>
      )}

      {request.isError && (
        /* The server's words: an in-flight run, a missing extension, a fuzzy tolerance on a source
           without a similarity function. Each is more useful than anything this screen could write. */
        <Alert tone="error" title={t('reconciliation.requestFailed')}>
          {request.error.message}
        </Alert>
      )}

      {definition.isError && (
        <Alert tone="error" title={t('reconciliation.loadFailed')}>
          {definition.error.message}
        </Alert>
      )}

      {definition.isPending && <SkeletonRows rows={2} columns={2} />}

      {definition.isSuccess && (
        /* Each side with its connection and every key column. A composite key is what makes
           `keys_not_unique` and `keys_disjoint` diagnosable, so all of it is shown. */
        <div className="lq-rcd__sides">
          {(
            [
              ['left', definition.data.left, definition.data.connection_id],
              [
                'right',
                definition.data.right,
                definition.data.right_connection_id ?? definition.data.connection_id,
              ],
            ] as const
          ).map(([side, spec, connectionId]) => (
            <div key={side} className="lq-card lq-rcd__side">
              <div className="lq-rcd__sideLabel">
                {t(`reconciliation.side.${side}`, {
                  connection: connectionName(connectionId),
                })}
              </div>
              <div className="lq-rcd__sideName lq-mono">
                <DatasetKindIcon
                  connectionId={connectionId}
                  qualifiedName={spec.qualified_name}
                />
                {spec.qualified_name}
              </div>
              <div className="lq-rcd__sideKeys">
                {t('reconciliation.side.keys')}{' '}
                <span className="lq-mono">{spec.key_columns.join(', ')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {runs.isSuccess && runs.data.truncated && (
        <div className="lq-band lq-band--warn">
          <div className="lq-band__title">{t('reconciliation.truncated.title')}</div>
          <div className="lq-band__body">{t('reconciliation.truncated.runs')}</div>
        </div>
      )}

      {runs.isPending && <SkeletonRows rows={4} columns={6} />}

      {runs.isError && (
        <Alert tone="error" title={t('reconciliation.runsFailed')}>
          {runs.error.message}
        </Alert>
      )}

      {runs.isSuccess && (
        <Card flush>
          <DataTable
            caption={t('reconciliation.runsCaption', { name: name ?? definitionId })}
            columns={[
              { key: 'requested', label: t('reconciliation.column.requested') },
              { key: 'state', label: t('reconciliation.column.state') },
              { key: 'matched', label: t('reconciliation.column.outcome.matched') },
              { key: 'mismatched', label: t('reconciliation.column.outcome.mismatched') },
              { key: 'missing', label: t('reconciliation.column.outcome.missing') },
              { key: 'jaccard', label: t('reconciliation.column.jaccard') },
              { key: 'judgedBy', label: t('reconciliation.column.judgedBy') },
              { key: 'breaks', label: '', actions: true },
            ]}
          >
            {runItems.map((run) => {
              const outcome = outcomeOf(run);
              const total = breakTotalOf(run) ?? 0;
              const when = ageText(t, run.requested_at, now) ?? run.requested_at;
              return (
                <tr key={run.id} className="lq-rdf__run">
                  <td className="lq-nowrap">
                    <div title={new Date(run.requested_at).toLocaleString()}>{when}</div>
                    <div className="lq-cell__secondary">
                      {personName(run.requested_by)}
                    </div>
                  </td>
                  <td>
                    <RunStateTag state={run.state} />
                    {typeof run.failure === 'string' && run.failure.length > 0 && (
                      <div className="lq-rdf__failure">{run.failure}</div>
                    )}
                  </td>
                  <td>
                    <Count value={outcome?.matched ?? null} />
                  </td>
                  <td>
                    <Count value={outcome?.mismatched ?? null} />
                  </td>
                  <td>
                    <Count value={outcome?.missing ?? null} />
                  </td>
                  <td>
                    <JaccardCell run={run} />
                  </td>
                  <td
                    className="lq-rdf__judged lq-nowrap"
                    title={run.tolerance_version_id}
                  >
                    {/* The stored citation is a UUID. The version number is what a reader wants, and
                        it is only correct to print when the run cites the version this page shows —
                        an older run may cite a superseded version whose number this route does not
                        carry, so that run shows the id itself, truncated. */}
                    {version !== null && version.id === run.tolerance_version_id
                      ? t('reconciliation.judgedBy', { version: version.version })
                      : `${run.tolerance_version_id.slice(0, 8)}…`}
                  </td>
                  <td className="lq-table__actions">
                    {hasBreaks(run) ? (
                      <Link
                        className="lq-btn lq-btn--secondary lq-btn--compact"
                        to={breaksPath(definitionId, run.id)}
                      >
                        {t('reconciliation.viewBreaks', {
                          count: total,
                          shown: compactNumber(total),
                        })}
                      </Link>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </DataTable>
          {runItems.length === 0 ? (
            <p className="lq-rdf__never">{t('reconciliation.runs.never')}</p>
          ) : null}
        </Card>
      )}

      {definition.isSuccess && (
        <div className="lq-rcd__panels">
          <TolerancePanel definition={definition.data} canPublish={mayPublish} />
          <BreakPolicyPanel
            definitionId={definitionId}
            canConfigure={mayConfigurePolicy}
          />
          <RunSchedulePanel
            definitionId={definitionId}
            canConfigure={mayConfigureSchedule}
          />
          <DefinitionHistory definitionId={definitionId} />
        </div>
      )}

      {runId !== undefined && (
        <BreaksDrawer
          definitionId={definitionId}
          runId={runId}
          definitionName={name ?? definitionId}
          onClose={() => {
            navigate(definitionPath(definitionId));
          }}
        />
      )}
    </div>
  );
}
